import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

interface BuilderWizardContextType {
    setSelectedService: Function;
    selectedService: string | null;
    setServiceItemsData: Function;
    serviceItemsData: Data | null;
    setValidationErrors: Function;
    validationErrors: Data | null | boolean;
    setDeclarationData: React.Dispatch<React.SetStateAction<Data | null>>;
    declarationData: Data | null;
    selectedApplication: string | null;
    setSelectedApplication: React.Dispatch<React.SetStateAction<string | null>>;
    isCurrentStepValid: Boolean;
    setIsCurrentStepValid: React.Dispatch<React.SetStateAction<boolean>>;
    currentStep: number;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    hasUnsavedChanges: boolean;
    setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
    checkForUnsavedChanges: () => boolean;
    saveLastSubmittedState: () => void;
    setInitialLastSubmittedState: (data: Data | null) => void;
}

interface ApplicationSelectItem {
    name: string;
}

interface Data {
    [key: string]: any;
}

// Create a context to manage shared data between wizard steps
const BuilderWizardContext = createContext<BuilderWizardContextType>({} as BuilderWizardContextType);

export const STORAGE_KEY = 'submissionBuilderState';
export const LAST_SUBMITTED_KEY = 'lastSubmittedState';

// Initial state
const initialBuilderState = {
    selectedApplication: null,
    selectedService: null,
    serviceItemsData: null,
    declarationData: null,
    validationErrors: null,
    isCurrentStepValid: false,
    currentStep: 0
};

// Helper function to get state from localStorage
const getStoredState = () => {
    try {
        const storedState = localStorage.getItem(STORAGE_KEY);
        return storedState ? JSON.parse(storedState) : initialBuilderState;
    } catch (error) {
        console.error('Error reading from localStorage:', error);
        return initialBuilderState;
    }
};

// Debounce helper
const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

export const useBuilderWizardContext = () => {
    return useContext(BuilderWizardContext);
};

export const BuilderWizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Initialize state from localStorage
    const storedState = getStoredState();    
    const [selectedApplication, setSelectedApplication] = useState<string | null>(storedState.selectedApplication);
    const [selectedService, setSelectedService] = useState(storedState.selectedService);
    const [serviceItemsData, setServiceItemsData] = useState<Data | null>(storedState.serviceItemsData);
    const [declarationData, setDeclarationData] = useState<Data | null>(storedState.declarationData);
    const [validationErrors, setValidationErrors] = useState<Data | null | boolean>(storedState.validationErrors);
    const [isCurrentStepValid, setIsCurrentStepValid] = useState(storedState.isCurrentStepValid);
    const [currentStep, setCurrentStep] = useState(storedState.currentStep);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const saveToStorage = useCallback(
        debounce((state: any) => {
            try {
                if (!state.declarationData) return;
                localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
            } catch (error) {
                console.error('Error saving to localStorage:', error);
            }
        }, 300), // 300ms debounce
        []
    );

    // Create a function inside the component to create wrapped setters
    const createStorageSyncedSetter = useCallback(<T,>(
        setter: React.Dispatch<React.SetStateAction<T>>,
        stateKey: string
    ) => {
        return (value: T | ((prev: T) => T)) => {
            setter(value);
            
            // Only save direct values, not function updaters
            if (typeof value !== 'function') {
                setTimeout(() => {
                    // Add index signature to allow dynamic property access
                    const currentState: { [key: string]: any } = {
                        selectedApplication,
                        selectedService,
                        serviceItemsData,
                        declarationData,
                        validationErrors,
                        isCurrentStepValid,
                        currentStep
                    };
                    currentState[stateKey] = value;
                    saveToStorage(currentState);
                }, 0);
            }
        };
    }, [
        selectedApplication,
        selectedService,
        serviceItemsData,
        declarationData,
        validationErrors,
        isCurrentStepValid,
        currentStep,
        saveToStorage
    ]);

    // Create wrapped setters
    const wrappedSetDeclarationData = createStorageSyncedSetter(
        setDeclarationData,
        'declarationData'
    );
    
    const wrappedSetValidationErrors = createStorageSyncedSetter(
        setValidationErrors,
        'validationErrors'
    );

    // Save last submitted state
    const saveLastSubmittedState = useCallback(() => {
        try {
            localStorage.setItem(LAST_SUBMITTED_KEY, JSON.stringify(declarationData));
        } catch (error) {
            console.error('Error saving last submitted state:', error);
        }
        // clear state from local storage on submission
        localStorage.removeItem(STORAGE_KEY);

    }, [declarationData]);

    // Function to check if there are unsaved changes
    const checkForUnsavedChanges = useCallback(() => {
        try {
            const lastSubmitted = localStorage.getItem(LAST_SUBMITTED_KEY);
            if (!lastSubmitted || !declarationData) return false;
            return JSON.stringify(declarationData) !== lastSubmitted;
        } catch (error) {
            console.error('Error checking for unsaved changes:', error);
            return false;
        }
    }, [declarationData]);

    // Add a separate effect just for checking unsaved changes
    useEffect(() => {
        if (declarationData) {
            const hasChanges = checkForUnsavedChanges();
            setHasUnsavedChanges(hasChanges);
        }
    }, [declarationData, checkForUnsavedChanges]);


    // Function to set the baseline "last submitted" state (called on initial load)
    const setInitialLastSubmittedState = useCallback((data: Data | null) => {
        try {
            localStorage.setItem(LAST_SUBMITTED_KEY, JSON.stringify(data));
        } catch (error) {
            console.error('Error setting initial last submitted state:', error);
        }
    }, []); // No dependencies needed

    const context: BuilderWizardContextType =  useMemo(() => ({
        selectedApplication,
        serviceItemsData,
        setServiceItemsData,
        setSelectedApplication,
        setSelectedService,
        selectedService,
        isCurrentStepValid,
        setIsCurrentStepValid,
        setCurrentStep,
        currentStep,
        setDeclarationData: wrappedSetDeclarationData,
        declarationData,
        validationErrors,
        setValidationErrors: wrappedSetValidationErrors,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        checkForUnsavedChanges,
        saveLastSubmittedState,
        setInitialLastSubmittedState
    }), [
        selectedApplication,
        serviceItemsData,
        selectedService,
        isCurrentStepValid,
        currentStep,
        wrappedSetDeclarationData,
        declarationData,
        validationErrors,
        wrappedSetValidationErrors,
        hasUnsavedChanges,
        checkForUnsavedChanges,
        setInitialLastSubmittedState
    ]);

    return (
        <BuilderWizardContext.Provider value={context}>
            {children}
        </BuilderWizardContext.Provider>
    );
};